import { WeightUnit } from "@redotech/cockroachdb-codegen/tables/product/weight-unit";
import { Currency } from "@redotech/money/currencies";
import {
  addWeight,
  convertWeight,
  Weight,
  WeightSchema,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum HSCodeSource {
  CLASSIFICATION = "CLASSIFICATION",
  MARKETPLACE = "MARKETPLACE",
  MANUAL = "MANUAL",
  CHECKOUT_QUOTE = "CHECKOUT_QUOTE", // Just zonos for now, but could come from any source
}

// Mirrors ManufacturerRecord.address (product-customs-facts.ts), which lives in a
// service package this common module can't depend on. Kept structurally identical.
const ManufacturerAddressSchema_ = z.object({
  street1: z
    .string()
    .meta({ privacy: privacy.address_line })
    .nullish()
    .meta({ privacy: privacy.address_line }),
  street2: z
    .string()
    .meta({ privacy: privacy.address_line })
    .nullish()
    .meta({ privacy: privacy.address_line }),
  city: z
    .string()
    .meta({ privacy: privacy.nonPii })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  state: z
    .string()
    .meta({ privacy: privacy.nonPii })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  postalCode: z
    .string()
    .meta({ privacy: privacy.nonPii })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  country: createEnum(RedoShopifyCountryCode, {
    enumName: "RedoShopifyCountryCode",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
});
export const ManufacturerAddressSchema: z.ZodType<ManufacturerAddress> =
  ManufacturerAddressSchema_;
export interface ManufacturerAddress extends z.infer<
  typeof ManufacturerAddressSchema_
> {}

const CustomsDeclarationSchema_ = z.object({
  description: z.string().meta({ privacy: privacy.nonPii }),
  quantity: z.number().meta({ privacy: privacy.nonPii }),
  itemValue: z.number().meta({ privacy: privacy.nonPii }),
  currency: createEnum(Currency, { enumName: "Currency" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  itemWeight: WeightSchema.meta({ privacy: privacy.structured }),
  harmonizationCode: z
    .string()
    .meta({ privacy: privacy.nonPii })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  hsCodeSource: createEnum(HSCodeSource, { enumName: "HSCodeSource" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  countryOfOrigin: createEnum(RedoShopifyCountryCode, {
    enumName: "RedoShopifyCountryCode",
  }).meta({ privacy: privacy.nonPii }),
  // Manufacturer company / contact name.
  manufacturer: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  // Manufacturer Identification Number (MID); some carriers (e.g. Chit Chats
  // U.S. DDP) accept it and otherwise auto-generate one.
  manufacturerMid: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  manufacturerAddress: ManufacturerAddressSchema_.nullish().meta({
    privacy: privacy.structured,
  }),
  productIdentifier: z.string().meta({ privacy: privacy.nonPii }).nullish(),
});
export const CustomsDeclarationSchema: z.ZodType<CustomsDeclaration> =
  CustomsDeclarationSchema_;
export interface CustomsDeclaration extends z.infer<
  typeof CustomsDeclarationSchema_
> {}
export interface ICustomsDeclaration extends z.infer<
  typeof CustomsDeclarationSchema_
> {}

export function customsDeclarationsAreEqual({
  a,
  b,
}: {
  a?: CustomsDeclaration | null;
  b?: CustomsDeclaration | null;
}) {
  if (!a || !b) {
    return false;
  }

  return (
    a.description === b.description &&
    a.quantity === b.quantity &&
    a.itemValue === b.itemValue &&
    a.manufacturer == b.manufacturer &&
    a.manufacturerMid == b.manufacturerMid &&
    manufacturerAddressesAreEqual(
      a.manufacturerAddress,
      b.manufacturerAddress,
    ) &&
    a.productIdentifier == b.productIdentifier &&
    convertWeight(a.itemWeight, WeightUnit.Oz).value ===
      convertWeight(b.itemWeight, WeightUnit.Oz).value &&
    a.harmonizationCode === b.harmonizationCode &&
    a.countryOfOrigin === b.countryOfOrigin
  );
}

function manufacturerAddressesAreEqual(
  a?: ManufacturerAddress | null,
  b?: ManufacturerAddress | null,
): boolean {
  return (
    (a?.street1 ?? null) === (b?.street1 ?? null) &&
    (a?.street2 ?? null) === (b?.street2 ?? null) &&
    (a?.city ?? null) === (b?.city ?? null) &&
    (a?.state ?? null) === (b?.state ?? null) &&
    (a?.postalCode ?? null) === (b?.postalCode ?? null) &&
    (a?.country ?? null) === (b?.country ?? null)
  );
}

export function getCustomsDeclarationsTotalWeight(
  customsDeclarations: CustomsDeclaration[],
): Weight {
  return customsDeclarations.reduce(
    (total: Weight, declaration) => {
      if (declaration.quantity > 0) {
        return addWeight(total, {
          unit: WeightUnit.Oz,
          value: declaration.itemWeight.value * declaration.quantity,
        });
      }
      return total;
    },
    { unit: WeightUnit.Oz, value: 0 },
  );
}
